import React, { useEffect, useMemo, useState } from 'react';
import '../admin.css';
import { Card, Typography, Button, Space, List, Tag, Table, message } from 'antd';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const { Title } = Typography;

const Subscription = () => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null); // { plan_name, started_at, expires_at }
  const [transactions, setTransactions] = useState([]);
  const [profile, setProfile] = useState(null);
  const [checkoutPlanId, setCheckoutPlanId] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactPlanName, setContactPlanName] = useState('White Label');
  const [industryOption, setIndustryOption] = useState('');
  const [industryOther, setIndustryOther] = useState('');
  const [contactForm, setContactForm] = useState({
    company_name: '',
    contact_name: '',
    business_email: '',
    phone: '',
    website: '',
    company_size: '',
    industry: '',
    message: '',
  });

  const COMPANY_SIZE_OPTIONS = ['', '1-10 employees', '11-50 employees', '51-200 employees', '201-1000 employees', '1000+ employees'];
  const INDUSTRY_OPTIONS = ['', 'E-commerce', 'SaaS', 'Agency', 'Media', 'Education', 'Health', 'Finance', 'Other'];

  const load = async () => {
    setLoading(true);
    try {
      // Current plan (self-service)
      try {
        const res = await fetch(`${apiBase}/my/current-plan`, { headers: { ...authHeaders() } });
        const body = await res.json().catch(() => (null));
        if (res.ok) setCurrent(body);
      } catch {}

      // Current user profile (for checkout session)
      try {
        const res = await fetch(`${apiBase}/me`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
        const body = await res.json().catch(() => (null));
        if (res.ok && body) setProfile(body);
      } catch {}

      // Available plans (public endpoint)
      try {
        const res = await fetch(`${apiBase}/public/subscription-plans`);
        const body = await res.json().catch(() => ({}));
        const items = Array.isArray(body?.items)
          ? body.items
          : Array.isArray(body)
            ? body
            : [];
        const allowedPlans = ['starter', 'growth', 'enterprise', 'white label'];
        const filtered = items.filter((item) => {
          if (!item?.plan_name) return false;
          const lowerName = item.plan_name.toLowerCase();
          return allowedPlans.some(allowed => lowerName.includes(allowed));
        });
        // Remove duplicates by plan name (keep first occurrence)
        const uniquePlans = [];
        const seenNames = new Set();
        for (const plan of filtered) {
          const lowerName = plan.plan_name.toLowerCase();
          if (!seenNames.has(lowerName)) {
            seenNames.add(lowerName);
            uniquePlans.push(plan);
          }
        }
        setPlans(uniquePlans);
      } catch {}

      // Billing transactions
      try {
        const res = await fetch(`${apiBase}/my/billing/transactions?limit=50`, { headers: { ...authHeaders() } });
        const body = await res.json().catch(() => ({}));
        setTransactions(Array.isArray(body?.items) ? body.items : []);
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openContactModal = (planName = 'White Label') => {
    setContactPlanName(planName);
    setShowContactModal(true);
    setContactError('');
    setContactSuccess(false);
  };

  const closeContactModal = () => {
    if (contactSubmitting) return;
    setShowContactModal(false);
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleIndustryChange = (e) => {
    const value = e.target.value;
    setIndustryOption(value);
    if (value === 'Other') {
      setIndustryOther('');
      setContactForm((prev) => ({ ...prev, industry: '' }));
    } else {
      setIndustryOther('');
      setContactForm((prev) => ({ ...prev, industry: value }));
    }
  };

  const handleIndustryOtherChange = (e) => {
    const value = e.target.value;
    setIndustryOther(value);
    setContactForm((prev) => ({ ...prev, industry: value }));
  };

  const submitContactForm = async (e) => {
    e.preventDefault();
    if (!contactForm.company_name || !contactForm.business_email) {
      setContactError('Please provide your company name and business email.');
      return;
    }

    setContactSubmitting(true);
    setContactError('');
    setContactSuccess(false);
    try {
      const rawWebsite = (contactForm.website || '').trim();
      const normalizedWebsite = rawWebsite ? `https://${rawWebsite.replace(/^https?:\/\//i, '')}` : '';
      const res = await fetch(`${apiBase}/sales/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...contactForm,
          website: normalizedWebsite,
          plan: contactPlanName,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.error) || 'Failed to send enquiry');
      setContactSuccess(true);
      setContactForm({
        company_name: '',
        contact_name: '',
        business_email: '',
        phone: '',
        website: '',
        company_size: '',
        industry: '',
        message: '',
      });
      setIndustryOption('');
      setIndustryOther('');
    } catch (err) {
      setContactError(err.message || 'Failed to send enquiry');
    } finally {
      setContactSubmitting(false);
    }
  };

  const startCheckout = async (plan) => {
    if (!profile?.id || !profile?.email) {
      message.error('Unable to determine your account details. Please refresh.');
      return;
    }

    if (!plan?.stripe_price_id) {
      message.info('Contact sales to purchase this plan.');
      return;
    }

    setCheckoutPlanId(plan.id);
    try {
      const res = await fetch(`${apiBase}/my/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          priceId: plan.stripe_price_id,
          planId: plan.id,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to start checkout');
      if (body?.url) {
        window.location.href = body.url;
      } else {
        throw new Error('Checkout URL missing');
      }
    } catch (e) {
      message.error(e.message || 'Failed to start checkout');
    } finally {
      setCheckoutPlanId(null);
    }
  };

  const columns = useMemo(() => ([
    { title: 'Date', dataIndex: 'processed_at', key: 'processed_at', render: (t) => t ? new Date(t).toLocaleString() : '—' },
    { title: 'Type', dataIndex: 'transaction_type', key: 'transaction_type', render: (t) => <Tag color="blue">{t}</Tag> },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (a, r) => (a != null ? `£${a}` : '—') },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => <Tag color={s === 'completed' ? 'green' : s === 'pending' ? 'gold' : 'red'}>{s}</Tag> },
    { title: 'Description', dataIndex: 'description', key: 'description' },
  ]), []);

  return (
    <div className="abo-content">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>Subscription & Billing</Title>
          <div style={{ color: '#64748b', fontSize: 13 }}>Manage your plan, payments and history</div>
        </div>
        <Button onClick={load} loading={loading}>Refresh</Button>
      </div>

      {/* Current Plan */}
      <Card hoverable style={{ borderRadius: 14, marginBottom: 16 }} bodyStyle={{ padding: 20 }} loading={loading}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Current Plan</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>{current?.plan_name || '—'}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {current?.started_at && `Started ${new Date(current.started_at).toLocaleDateString()}`}
              {current?.expires_at && ` • Ends ${new Date(current.expires_at).toLocaleDateString()}`}
            </div>
          </div>
        </div>
      </Card>

      {/* Available Plans */}
      <Card hoverable style={{ borderRadius: 14, marginBottom: 16 }} bodyStyle={{ padding: 20 }} loading={loading}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>Available Plans</div>
        </div>
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 3 }}
          dataSource={plans}
          renderItem={(p) => {
            const lowerName = p.plan_name?.toLowerCase() || '';
            const isCurrent = current?.plan_name && current.plan_name.toLowerCase() === lowerName;
            const isWhiteLabel = lowerName.includes('white');
            const isContactSalesOnly = isWhiteLabel || lowerName === 'enterprise';
            return (
              <List.Item key={p.id}>
                <Card hoverable bodyStyle={{ padding: 16 }} style={{ borderRadius: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{p.plan_name}</div>
                    {isCurrent && <Tag color="green" style={{ margin: 0 }}>Current</Tag>}
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                    {isContactSalesOnly
                      ? 'Contact Sales'
                      : (p.monthly_price != null ? `£${p.monthly_price}/mo` : 'Contact us')}
                  </div>
                  <Space>
                    <Button
                      type="primary"
                      disabled={isCurrent || (!p.stripe_price_id && !isWhiteLabel)}
                      loading={!isWhiteLabel && checkoutPlanId === p.id}
                      onClick={() => (isWhiteLabel ? openContactModal(p.plan_name) : startCheckout(p))}
                    >
                      {isCurrent
                        ? 'Current Plan'
                        : isContactSalesOnly
                          ? 'Contact Sales'
                          : (p.stripe_price_id ? 'Proceed to Checkout' : 'Contact Sales')}
                    </Button>
                  </Space>
                </Card>
              </List.Item>
            );
          }}
        />
      </Card>

      {/* Billing History */}
      <Card hoverable style={{ borderRadius: 14 }} bodyStyle={{ padding: 20 }} loading={loading}>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Billing History</div>
        <Table
          rowKey="id"
          dataSource={transactions}
          columns={columns}
          size="small"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {showContactModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px 0',
            zIndex: 1000,
            overflowY: 'auto',
          }}
          onClick={closeContactModal}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 520,
              margin: '0 16px',
              background: '#ffffff',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 24px 70px rgba(15,23,42,0.35)',
              border: '1px solid #e2e8f0',
              color: '#0f172a',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <p style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{contactPlanName}</p>
                <h3 style={{ margin: 0 }}>Talk to sales</h3>
              </div>
              <button
                type="button"
                onClick={closeContactModal}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: 20,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <p style={{ marginBottom: 16, fontSize: 14, color: '#475569' }}>
              Share a few details about your business and we will reach out with {contactPlanName} pricing and rollout options.
            </p>
            <form onSubmit={submitContactForm}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Company name*</label>
                  <input
                    type="text"
                    name="company_name"
                    value={contactForm.company_name}
                    onChange={handleContactChange}
                    required
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5f5', background: '#fff' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Contact name</label>
                  <input
                    type="text"
                    name="contact_name"
                    value={contactForm.contact_name}
                    onChange={handleContactChange}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5f5', background: '#fff' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Business email*</label>
                  <input
                    type="email"
                    name="business_email"
                    value={contactForm.business_email}
                    onChange={handleContactChange}
                    required
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5f5', background: '#fff' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleContactChange}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5f5', background: '#fff' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Website</label>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5f5', background: '#f8fafc', color: '#64748b', fontSize: 13 }}>https://</span>
                    <input
                      type="text"
                      name="website"
                      value={contactForm.website}
                      onChange={handleContactChange}
                      placeholder="yourdomain.com"
                      style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5f5', background: '#fff' }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Company size</label>
                  <select
                    name="company_size"
                    value={contactForm.company_size}
                    onChange={handleContactChange}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5f5', background: '#fff' }}
                  >
                    {COMPANY_SIZE_OPTIONS.map((opt) => (
                      <option key={opt || 'empty'} value={opt}>
                        {opt || 'Select company size'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Industry</label>
                  <select
                    name="industry_option"
                    value={industryOption}
                    onChange={handleIndustryChange}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5f5', background: '#fff' }}
                  >
                    {INDUSTRY_OPTIONS.map((opt) => (
                      <option key={opt || 'empty'} value={opt}>
                        {opt || 'Select industry'}
                      </option>
                    ))}
                  </select>
                  {industryOption === 'Other' && (
                    <input
                      type="text"
                      name="industry_other"
                      value={industryOther}
                      onChange={handleIndustryOtherChange}
                      placeholder="Please specify your industry"
                      style={{ marginTop: 8, width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5f5', background: '#fff' }}
                    />
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Anything else we should know?</label>
                  <textarea
                    name="message"
                    rows={4}
                    value={contactForm.message}
                    onChange={handleContactChange}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5f5', background: '#fff', resize: 'vertical' }}
                  />
                </div>
              </div>
              {contactError && <p style={{ marginTop: 10, fontSize: 13, color: '#ef4444' }}>{contactError}</p>}
              {contactSuccess && <p style={{ marginTop: 10, fontSize: 13, color: '#16a34a' }}>Thank you. Your request has been sent to our sales team.</p>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                <button type="button" onClick={closeContactModal} disabled={contactSubmitting} className="btn btn-ghost">
                  Cancel
                </button>
                <button type="submit" disabled={contactSubmitting} className="button" style={{ minWidth: 140 }}>
                  {contactSubmitting ? 'Sending…' : 'Send to sales'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
